var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * React Query hook for updating proposal data
 */
import { toast } from 'react-hot-toast';
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateProposal as updateProposalService } from '../services/ProposalServices';
import useProposalDetail from './useProposalDetail';
/**
 * Hook for updating proposal data with optimistic updates
 * @returns Object containing the updateProposal mutation
 */
export default function useUpdateProposal(proposalId) {
    const { data: proposal } = useProposalDetail(proposalId);
    const queryClient = useQueryClient();
    const updateProposal = useMutation(({ id, proposalUpdate }) => updateProposalService(id, proposalUpdate), {
        // When mutate is called:
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ id, proposalUpdate }) {
            var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            // Cancel any outgoing refetches to avoid overwriting optimistic update
            yield queryClient.cancelQueries(['proposal', id]);
            // Snapshot the previous value
            const previousProposal = queryClient.getQueryData(['proposal', id]);
            // Optimistically update to the new value
            if (previousProposal) {
                queryClient.setQueryData(['proposal', id], Object.assign(Object.assign(Object.assign({}, previousProposal), { building: proposalUpdate.buildingId
                        ? Object.assign(Object.assign({}, (previousProposal.building || {})), { id: Number(proposalUpdate.buildingId), name: proposalUpdate.buildingName || '', address: (_c = (_b = previousProposal.building) === null || _b === void 0 ? void 0 : _b.address) !== null && _c !== void 0 ? _c : '' }) : previousProposal.building, priceList: proposalUpdate.priceListId
                        ? Object.assign(Object.assign({}, (previousProposal.priceList || {})), { id: Number(proposalUpdate.priceListId), name: proposalUpdate.priceListName || '', active: (_e = (_d = previousProposal.priceList) === null || _d === void 0 ? void 0 : _d.active) !== null && _e !== void 0 ? _e : false }) : previousProposal.priceList, tax: proposalUpdate.taxRateId
                        ? Object.assign(Object.assign({}, (previousProposal.tax || {})), { id: Number(proposalUpdate.taxRateId), name: proposalUpdate.taxRateName || '', rate: (_g = (_f = previousProposal.tax) === null || _f === void 0 ? void 0 : _f.rate) !== null && _g !== void 0 ? _g : '', active: (_j = (_h = previousProposal.tax) === null || _h === void 0 ? void 0 : _h.active) !== null && _j !== void 0 ? _j : false }) : previousProposal.tax, preparedFor: proposalUpdate.preparedFor ||
                        proposalUpdate.preparedForEmail ||
                        proposalUpdate.preparedForPhone ||
                        proposalUpdate.preparedForJobTitle
                        ? {
                            name: proposalUpdate.preparedFor || ((_k = previousProposal.preparedFor) === null || _k === void 0 ? void 0 : _k.name) || '',
                            email: proposalUpdate.preparedForEmail || ((_l = previousProposal.preparedFor) === null || _l === void 0 ? void 0 : _l.email) || '',
                            phone: proposalUpdate.preparedForPhone || ((_m = previousProposal.preparedFor) === null || _m === void 0 ? void 0 : _m.phone) || '',
                            title: proposalUpdate.preparedForJobTitle || ((_o = previousProposal.preparedFor) === null || _o === void 0 ? void 0 : _o.title) || ''
                        }
                        : previousProposal.preparedFor }), Object.fromEntries(Object.entries(proposalUpdate).filter(([key]) => !['preparedFor', 'preparedForEmail', 'preparedForPhone', 'preparedForJobTitle'].includes(key)))));
            }
            // Return context with the previous value
            return { previousProposal };
        }),
        // If the mutation fails, use the context returned from onMutate to roll back
        onError: (err, { id }, context) => {
            if (context === null || context === void 0 ? void 0 : context.previousProposal) {
                queryClient.setQueryData(['proposal', id], context.previousProposal);
            }
            toast.error(`Failed to update proposal: ${err.message}`);
        },
        // Always refetch after error or success to ensure data is up to date
        onSettled: (_data, _error, { id }) => {
            void queryClient.invalidateQueries(['proposal', id]);
        },
        onSuccess: (updatedProposal) => {
            queryClient.setQueryData(['proposal', updatedProposal.id], updatedProposal);
            toast.success('Proposal updated successfully');
        }
    });
    const handleFieldUpdate = useCallback((updates) => {
        if (!(proposal === null || proposal === void 0 ? void 0 : proposal.id))
            return;
        // Filter out null or empty values
        const filteredUpdates = Object.entries(updates).reduce((acc, [key, val]) => {
            if (val !== null && val !== '') {
                acc[key] = val;
            }
            return acc;
        }, {});
        // If no valid updates after filtering, return early
        if (Object.keys(filteredUpdates).length === 0)
            return;
        updateProposal.mutate({
            id: proposal.id,
            proposalUpdate: filteredUpdates
        });
    }, [proposal === null || proposal === void 0 ? void 0 : proposal.id, updateProposal]);
    const handleAddEntry = useCallback((fieldType, updatedEntry) => {
        if (!(proposal === null || proposal === void 0 ? void 0 : proposal.id))
            return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        const currentEntries = proposal[`${fieldType}Entries`] || [];
        const updatedEntries = [...currentEntries, updatedEntry];
        updateProposal.mutate({
            id: proposal.id,
            proposalUpdate: { [`${fieldType}Entries`]: updatedEntries }
        });
    }, [proposal === null || proposal === void 0 ? void 0 : proposal.id, updateProposal]);
    const handleUpdateEntry = useCallback((fieldType, updatedEntry) => {
        if (!(proposal === null || proposal === void 0 ? void 0 : proposal.id))
            return;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const currentEntries = proposal[`${fieldType}Entries`] || [];
        const updatedEntries = currentEntries.map((entry) => entry.id === updatedEntry.id ? updatedEntry : entry);
        updateProposal.mutate({
            id: proposal.id,
            proposalUpdate: { [`${fieldType}Entries`]: updatedEntries }
        });
    }, [proposal === null || proposal === void 0 ? void 0 : proposal.id, updateProposal]);
    const handleDeleteEntry = useCallback((fieldType, entryId) => {
        if (!(proposal === null || proposal === void 0 ? void 0 : proposal.id))
            return;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const currentEntries = proposal[`${fieldType}Entries`] || [];
        const updatedEntries = currentEntries.filter((entry) => entry.id !== entryId);
        updateProposal.mutate({
            id: proposal.id,
            proposalUpdate: { [`${fieldType}Entries`]: updatedEntries }
        });
    }, [proposal === null || proposal === void 0 ? void 0 : proposal.id, updateProposal]);
    return { updateProposal, handleFieldUpdate, handleAddEntry, handleUpdateEntry, handleDeleteEntry };
}
